import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const CitizenModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [devOtp, setDevOtp] = useState('');
  const navigate = useNavigate();

  const reset = () => {
    setStep(1);
    setName('');
    setMobile('');
    setOtp('');
    setError('');
    setSuccess('');
    setLoading(false);
    setOtpSent(false);
    setDevOtp('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSendOtp = async () => {
    setError('');
    if (!name.trim()) { setError('Please enter your full name.'); return; }
    if (!/^\d{10}$/.test(mobile.trim())) { setError('Enter a valid 10-digit mobile number.'); return; }

    setLoading(true);
    try {
      const { data } = await api.post('/auth/citizen/send-otp', { mobile: mobile.trim() });
      if (data.data?.devOtp) {
        setDevOtp(data.data.devOtp);
        setOtp(data.data.devOtp);
      }
      setOtpSent(true);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.message || 'Backend se connect nahi ho pa raha. Server chalu hai?');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError('');
    if (otp.length !== 6) { setError('Enter the 6-digit OTP sent to your mobile.'); return; }

    setLoading(true);
    try {
      const { data } = await api.post('/auth/citizen/verify-otp', {
        mobile: mobile.trim(),
        otp,
        name: name.trim(),
      });
      localStorage.setItem('citizen_user', name.trim());
      localStorage.setItem('citizen_mobile', mobile.trim());
      if (data.data?.token) localStorage.setItem('citizen_token', data.data.token);
      setSuccess(`Welcome, ${name.trim()}! Redirecting to your dashboard…`);
      setTimeout(() => {
        handleClose();
        navigate('/citizen-dashboard');
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (e) => {
    const val = e.target.value.replace(/\D/g, '');
    if (val.length <= 6) setOtp(val);
  };

  return (
    <div
      aria-modal="true"
      role="dialog"
      aria-label="Citizen Portal"
      className={`fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-950/90 backdrop-blur-xl transition-all duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className={`relative w-full sm:max-w-md transition-all duration-300 ${isOpen ? 'scale-100 translate-y-0 opacity-100' : 'scale-95 translate-y-5 opacity-0'}`}>
        <div className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-cyan-400/80 to-transparent" />

        <div className="relative bg-white border border-cyan-500/20 rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl shadow-cyan-950/20">
          <div className="absolute -top-10 -right-10 w-56 h-56 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" />
          <div className="h-[3px] w-full bg-gradient-to-r from-cyan-600 via-emerald-400 to-cyan-600" />

          <div className="p-7 sm:p-8 relative">
            <button
              type="button"
              aria-label="Close modal"
              onClick={handleClose}
              className="absolute top-5 right-5 w-8 h-8 rounded-lg bg-black/5 hover:bg-black/10 border border-black/10 flex items-center justify-center text-gray-500 hover:text-black transition-all"
            >
              <i className="fa-solid fa-xmark text-sm" />
            </button>

            <div className="flex items-center gap-4 mb-6">
              <div className="relative shrink-0">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center shadow-lg shadow-cyan-500/25">
                  <i className="fa-solid fa-id-card text-xl text-white" />
                </div>
                <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-cyan-400 rounded-full border-2 border-white flex items-center justify-center">
                  <i className="fa-solid fa-bolt text-[8px] text-black" />
                </span>
              </div>
              <div>
                <h2 className="text-xl font-black text-black rajdhani tracking-wide leading-tight">Citizen Portal</h2>
                <p className="text-gray-500 text-xs flex items-center gap-1.5 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 inline-block animate-pulse" />
                  Public Cyber Services &amp; Complaint Access
                </p>
              </div>
            </div>


            {/* Step indicators */}
            <div className="flex items-center gap-2 mb-6">
              <div className={`flex items-center gap-1.5 ${step >= 1 ? 'text-cyan-400' : 'text-gray-500'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-black border ${step >= 1 ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400' : 'border-gray-300 text-gray-500'}`}>1</div>
                <span className="text-[11px] font-semibold tracking-wide">Details</span>
              </div>
              <div className={`flex-1 h-px ${step >= 2 ? 'bg-cyan-500/40' : 'bg-black/10'}`} />
              <div className={`flex items-center gap-1.5 ${step >= 2 ? 'text-cyan-400' : 'text-gray-500'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-black border ${step >= 2 ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400' : 'border-gray-300 text-gray-500'}`}>2</div>
                <span className="text-[11px] font-semibold tracking-wide">OTP Verify</span>
              </div>
            </div>

            {success ? (
              <div className="text-center py-8">
                <div className="w-20 h-20 rounded-full bg-emerald-500/12 border-2 border-emerald-500/30 flex items-center justify-center mx-auto mb-5">
                  <i className="fa-solid fa-circle-check text-4xl text-emerald-400" />
                </div>
                <h3 className="text-lg font-black text-black rajdhani tracking-wide mb-2">Verified Successfully!</h3>
                <p className="text-sm text-emerald-300 leading-relaxed px-4">{success}</p>
                <p className="text-xs text-gray-600 mt-4 flex items-center justify-center gap-2">
                  <svg className="animate-spin w-3 h-3 text-cyan-400 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Redirecting to your dashboard…
                </p>
              </div>
            ) : step === 1 ? (
              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block mb-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-widest">Full Name</label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none">
                      <i className="fa-solid fa-user text-sm" />
                    </span>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
                      placeholder="Enter your full name"
                      disabled={loading}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-10 pr-4 py-3 text-black text-sm placeholder-gray-400 outline-none focus:border-cyan-500/60 focus:bg-white focus:ring-2 focus:ring-cyan-500/12 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>

                {/* Mobile */}
                <div>
                  <label className="block mb-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-widest">Mobile Number</label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none">
                      <i className="fa-solid fa-mobile-screen text-sm" />
                    </span>
                    <span className="absolute left-10 top-1/2 -translate-y-1/2 text-gray-500 text-xs border-r border-gray-300 pr-2.5 select-none">+91</span>
                    <input
                      type="tel"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                      onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
                      placeholder="10-digit mobile number"
                      disabled={loading}
                      maxLength={10}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-[4.5rem] pr-10 py-3 text-black text-sm placeholder-gray-400 outline-none focus:border-cyan-500/60 focus:bg-white focus:ring-2 focus:ring-cyan-500/12 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    {mobile.length === 10 && (
                      <span className="absolute right-3.5 top-1/2 -translate-y-1/2">
                        <i className="fa-solid fa-circle-check text-emerald-400 text-sm" />
                      </span>
                    )}
                  </div>
                </div>

                {error && (
                  <div className="flex items-start gap-2.5 bg-red-500/10 border border-red-500/25 rounded-xl px-3.5 py-2.5">
                    <i className="fa-solid fa-triangle-exclamation text-red-400 text-xs mt-0.5 shrink-0" />
                    <p className="text-xs text-red-300 leading-relaxed">{error}</p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={loading}
                  className={`relative w-full py-3.5 rounded-xl text-sm font-black rajdhani tracking-widest transition-all overflow-hidden mt-1 ${loading ? 'bg-cyan-700/60 cursor-not-allowed text-white/60' : 'bg-gradient-to-r from-cyan-500 to-emerald-500 text-black hover:shadow-xl hover:shadow-cyan-500/20 hover:scale-[1.015] active:scale-[0.99]'}`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2 text-white">
                      <svg className="animate-spin w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Sending OTP…
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2 font-black">
                      <i className="fa-solid fa-paper-plane" /> Send OTP
                    </span>
                  )}
                </button>

              </div>
            ) : (
              /* Step 2 — OTP */
              <div className="space-y-4">
                <div className="flex items-center gap-3 bg-black/[0.03] border border-black/10 rounded-xl px-4 py-3">
                  <i className="fa-solid fa-mobile-screen text-cyan-400 text-sm shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-gray-500 uppercase tracking-widest mb-0.5">OTP sent to</p>
                    <p className="text-sm text-black font-semibold truncate">+91 {mobile}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setStep(1); setOtp(''); setError(''); }}
                    className="text-[11px] text-cyan-600 hover:text-cyan-800 font-semibold shrink-0 transition-colors"
                  >
                    Change
                  </button>
                </div>

                {devOtp && (
                  <div className="flex items-center gap-2.5 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3.5 py-2.5">
                    <i className="fa-solid fa-triangle-exclamation text-amber-400 text-xs shrink-0" />
                    <p className="text-xs text-amber-300">
                      <span className="font-semibold">Dev Mode:</span> OTP auto-filled — <span className="font-black tracking-widest">{devOtp}</span>
                    </p>
                  </div>
                )}

                <div>
                  <label className="block mb-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-widest">Enter 6-digit OTP</label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none">
                      <i className="fa-solid fa-key text-sm" />
                    </span>
                    <input
                      type="tel"
                      value={otp}
                      onChange={handleOtpChange}
                      onKeyDown={(e) => e.key === 'Enter' && handleVerifyOtp()}
                      placeholder="• • • • • •"
                      disabled={loading}
                      maxLength={6}
                      autoFocus
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-10 pr-10 py-3 text-black text-sm placeholder-gray-400 outline-none focus:border-cyan-500/60 focus:bg-white focus:ring-2 focus:ring-cyan-500/12 transition-all tracking-[0.4em] text-center font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    {otp.length === 6 && (
                      <span className="absolute right-3.5 top-1/2 -translate-y-1/2">
                        <i className="fa-solid fa-circle-check text-emerald-400 text-sm" />
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-gray-600 mt-1.5 flex items-center gap-1">
                    <i className="fa-solid fa-clock text-[9px]" /> OTP valid for 10 minutes
                  </p>
                </div>

                {error && (
                  <div className="flex items-start gap-2.5 bg-red-500/10 border border-red-500/25 rounded-xl px-3.5 py-2.5">
                    <i className="fa-solid fa-triangle-exclamation text-red-400 text-xs mt-0.5 shrink-0" />
                    <p className="text-xs text-red-300 leading-relaxed">{error}</p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleVerifyOtp}
                  disabled={loading}
                  className={`relative w-full py-3.5 rounded-xl text-sm font-black rajdhani tracking-widest transition-all overflow-hidden mt-1 ${loading ? 'bg-cyan-700/60 cursor-not-allowed text-white/60' : 'bg-gradient-to-r from-cyan-500 to-emerald-500 text-black hover:shadow-xl hover:shadow-cyan-500/20 hover:scale-[1.015] active:scale-[0.99]'}`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2 text-white">
                      <svg className="animate-spin w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Verifying OTP…
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2 font-black">
                      <i className="fa-solid fa-shield-halved" /> Verify &amp; Enter Portal
                    </span>
                  )}
                </button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={loading}
                    className="text-[11px] text-gray-500 hover:text-cyan-400 transition-colors disabled:opacity-40"
                  >
                    Didn't receive OTP? <span className="text-cyan-400 font-semibold">Resend</span>
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default CitizenModal;
